import { useEffect, useState, useCallback, useMemo, useContext } from 'react';
import {
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  useTheme,
  IconButton,
  Stack,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import { Download, Sparkles, RefreshCw, TrendingUp, CheckCircle, Pill, ArrowRightLeft, ShieldOff, Stethoscope } from 'lucide-react';
import LabTrends from './LabTrends';
import { toast } from 'react-hot-toast';
import { ColorModeContext } from '../../App';
import { Patient, Visit, Prescription, Appointment } from '../../types';
import { usePatients } from '../../hooks/usePatients';
import { useDoctors } from '../../hooks/useQueries';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { PERM } from '../../lib/permissions';
import { useTenant } from '../../contexts/TenantContext';
import { usePatientDetails } from '../../contexts/PatientDetailsContext';
import { safeConsole } from '../../lib/piiProtection';
import { PrescriptionWizard } from '../prescriptions/PrescriptionWizard';
import { useWebSocket } from '../../hooks/useWebSocket';
import PrescriptionViewDialog from '../prescriptions/PrescriptionViewDialog';
import { LabUploadModal } from '../prescriptions/wizard/LabUploadModal';
import { useNavigate } from 'react-router-dom';
import { FollowUpModal } from '../tokens/FollowUpModal';
import { AccordionSection } from '../common/AccordionSection';
import { PatientMedicalInfoSection } from './PatientMedicalInfoSection';
import { PatientVisitHistorySection } from './PatientVisitHistorySection';
import { PatientPrescriptionsSection } from './PatientPrescriptionsSection';
import { PatientDiagnosticsSection } from './PatientDiagnosticsSection';
import { PatientAISummaryPanel } from './PatientAISummaryPanel';
import { PatientLabAnalysisDialog } from './PatientLabAnalysisDialog';
import { ConsentRequestDialog } from '../consent/ConsentRequestDialog';
import { SpecialtyCarePanel } from './SpecialtyCarePanel';
import { useSpecialtyTemplate } from '../../hooks/useSpecialtyTemplate';
import { useTranslation } from 'react-i18next';

interface PatientProfileProps {
  patient: Patient;
  open: boolean;
  onClose: () => void;
  appointment?: Appointment;
  /** Accordion panel to expand first (e.g. 'readings' for labs when arriving from a lab notification). */
  initialSection?: string;
}

const DRAWER_WIDTH = 900;
const DRAWER_HEIGHT = 'calc(100vh - 160px)';

export function PatientProfile({ patient, open, onClose, appointment, initialSection }: PatientProfileProps) {
  const { t } = useTranslation('patients');
  const theme = useTheme();
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';
  const drawerWidth = DRAWER_WIDTH;
  const drawerHeight = DRAWER_HEIGHT;
  const { getPatientHistory } = usePatients();
  const { userData } = useAuth();
  const { can } = usePermissions();
  const { activeClinicId } = useTenant();
  const { minimizePatient } = usePatientDetails();

  // True when the patient is registered at the currently active clinic (staff can transfer/refer).
  // False when viewing a cross-clinic CONSENT_REQUIRED patient from another branch.
  const isOwnPatient = !patient.clinicId || patient.clinicId === activeClinicId;

  // Helper function to parse allergies/conditions (handle both string and array formats)
  const parseStringArray = (value: string | string[] | undefined): string[] => {
    if (!value) return [];
    if (typeof value === 'string') return value.split(',').map(v => v.trim());
    return Array.isArray(value) ? value : [];
  };
  
  const navigate = useNavigate();
  const [showConsentRequestDialog, setShowConsentRequestDialog] = useState(false);
  const [showErasureDialog, setShowErasureDialog] = useState(false);
  const [erasureReason, setErasureReason] = useState('');
  const [submittingErasure, setSubmittingErasure] = useState(false);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showLabUploadModal, setShowLabUploadModal] = useState(false);
  const [showVitalsForm, setShowVitalsForm] = useState(false);
  const [prescriptionWizardOpen, setPrescriptionWizardOpen] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState('');
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loadingPrescriptions, setLoadingPrescriptions] = useState(false);
  const [standaloneDocuments, setStandaloneDocuments] = useState<any[]>([]);
  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);
  const [showPrescriptionView, setShowPrescriptionView] = useState(false);
  const [processingPdfId, setProcessingPdfId] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [loadingAiSummary, setLoadingAiSummary] = useState(false);
  const [expanded, setExpanded] = useState<string | false>(initialSection || 'medical-info');
  const [isMaximized, setIsMaximized] = useState(false);

  const [analyzingLabId, setAnalyzingLabId] = useState<string | null>(null);
  const [labAnalysis, setLabAnalysis] = useState<any | null>(null);
  const [showLabAnalysisDialog, setShowLabAnalysisDialog] = useState(false);
  
  // Clinical information edit mode
  const [editingVitals, setEditingVitals] = useState(false);
  const [vitalsSaving, setVitalsSaving] = useState(false);
  const [, setVitalsHistoryLoading] = useState(false);
  const [displayPatient, setDisplayPatient] = useState<Patient>(patient);
  const [vitalFormData, setVitalFormData] = useState({
    bloodGroup: patient.bloodGroup || '',
    allergies: parseStringArray(patient.allergies),
    chronicConditions: parseStringArray(patient.chronicConditions),
  });
  const [allergyInput, setAllergyInput] = useState('');
  const [conditionInput, setConditionInput] = useState('');

  // Track if current visit has vitals
  const [currentVisitHasVitals, setCurrentVisitHasVitals] = useState(false);
  const { data: doctors = [] } = useDoctors();
  const doctorMap = useMemo(() => new Map(doctors.map((d) => [d.id, d.name])), [doctors]);
  const { templateKey: specialtyTemplateKey } = useSpecialtyTemplate(appointment?.doctorId);
  
  // WebSocket for real-time prescription updates
  const { isConnected, subscribe, unsubscribe } = useWebSocket();

  const loadPatientHistory = useCallback(async () => {
    setLoading(true);
    const history = await getPatientHistory(patient.id);
    setVisits(history);
    setLoading(false);

    // Check if current appointment has vitals
    if (appointment) {
      checkCurrentVisitVitals();
    }
  }, [patient.id, getPatientHistory, appointment]);

  const checkCurrentVisitVitals = async () => {
    if (!appointment?.id) return;

    setVitalsHistoryLoading(true);
    try {
      const response = await api.visits.getWithVitals(patient.id);
      if (response.data) {
        // Find visit matching current appointment by ID, or fall back to today's most recent visit
        const today = new Date().toISOString().split('T')[0];
        const currentVisit = response.data.find((v: any) => v.id === appointment.id)
          || response.data.find((v: any) => v.visitDate?.startsWith(today));
        const hasActualVitals = currentVisit && [
          currentVisit.systolicBP,
          currentVisit.diastolicBP,
          currentVisit.pulse,
          currentVisit.temperature,
          currentVisit.spO2,
          currentVisit.respiratoryRate,
          currentVisit.weight,
        ].some((v: any) => v !== null && v !== undefined);
        setCurrentVisitHasVitals(!!hasActualVitals);
      }
    } catch (error) {
      safeConsole.warn('Failed to check visit vitals:', error);
    } finally {
      setVitalsHistoryLoading(false);
    }
  };

  const sortedVisits = useMemo(() => {
    return [...visits].sort((a, b) => new Date(b.visitDate).getTime() - new Date(a.visitDate).getTime());
  }, [visits]);

  useEffect(() => {
    if (sortedVisits.length > 0 && !selectedVisitId) {
      setSelectedVisitId(sortedVisits[0].id);
    }
  }, [sortedVisits, selectedVisitId]);

  const loadPrescriptions = useCallback(async () => {
    if (!patient.id) return;
    
    setLoadingPrescriptions(true);
    try {
      const response = await api.prescriptions.getByPatient(patient.id);
      setPrescriptions(response.data || []);
    } catch (error) {
      safeConsole.error('Failed to load prescriptions:', error);
      setPrescriptions([]);
    } finally {
      setLoadingPrescriptions(false);
    }
  }, [patient.id]);

  const loadStandaloneDocuments = useCallback(async () => {
    if (!patient.id) return;
    try {
      const response = await api.labUpload.getPatientUploads(patient.id);
      setStandaloneDocuments(response.data || []);
    } catch (error) {
      safeConsole.error('Failed to load standalone documents:', error);
    }
  }, [patient.id]);

  const loadPatientInfo = useCallback(async () => {
    if (!patient.id) return;
    try {
      const response = await api.patients.getById(patient.id);
      if (response.data) {
        // Update displayPatient with fresh data including clinical fields
        setDisplayPatient(response.data);
        // Update vitalFormData to reflect latest data
        if (!editingVitals) {
          setVitalFormData({
            bloodGroup: response.data.bloodGroup || '',
            allergies: parseStringArray(response.data.allergies),
            chronicConditions: parseStringArray(response.data.chronicConditions),
          });
        }
      }
    } catch (error) {
      safeConsole.error('Failed to load patient info:', error);
    }
  }, [patient.id, editingVitals]);

  const loadAllData = useCallback(() => {
    loadPatientHistory();
    loadPrescriptions();
    loadStandaloneDocuments();
    loadPatientInfo();
  }, [loadPatientHistory, loadPrescriptions, loadStandaloneDocuments, loadPatientInfo]);

  const handleRefresh = () => {
    loadAllData();
  };

  useEffect(() => {
    if (open && patient?.id) {
      // Clear previous patient's AI data to prevent crosstalk
      setAiSummary(null);
      setLabAnalysis(null);
      setAnalyzingLabId(null);

      loadPatientHistory();
      loadPrescriptions();
      loadStandaloneDocuments();
      loadPatientInfo();
    }
  }, [open, patient?.id, loadPatientHistory, loadPrescriptions, loadStandaloneDocuments, loadPatientInfo]);

  // Sync patient prop to displayPatient state for vitals display
  useEffect(() => {
    setDisplayPatient(patient);
    // Update vitalFormData when patient prop changes (e.g., after parent reload)
    if (!editingVitals) {
      setVitalFormData({
        bloodGroup: patient.bloodGroup || '',
        allergies: parseStringArray(patient.allergies),
        chronicConditions: parseStringArray(patient.chronicConditions),
      });
    }
  }, [patient, editingVitals]);

  // WebSocket subscription for real-time prescription updates
  useEffect(() => {
    if (isConnected && open && patient?.id) {
      const handlePrescriptionNotification = (notification: any) => {
        const data = typeof notification === 'string' ? JSON.parse(notification) : notification;
        
        // Listen for prescription-related notifications
        if (data.type?.includes('PRESCRIPTION') || data.relatedEntityType === 'PRESCRIPTION') {
          console.log('✅ PatientProfile: Prescription update received:', data);
          // Small delay to ensure database commit is complete
          setTimeout(() => {
            loadPrescriptions();
          }, 500);
        }
      };

      subscribe('/user/queue/notifications', handlePrescriptionNotification);

      return () => {
        unsubscribe('/user/queue/notifications');
      };
    }
  }, [isConnected, open, patient?.id, subscribe, unsubscribe, loadPrescriptions]);

  // Medical VIEW is permission-gated (front desk lacks OPD_MEDICAL_RECORDS_VIEW); labs separate.
  const isSuper = userData?.role === 'super_admin';
  const canViewMedical = can(PERM.OPD_MEDICAL_RECORDS_VIEW);
  const canViewLabs = can(PERM.LAB_RESULTS_VIEW);
  // Intake/triage (blood group, allergies, pre-existing conditions, vitals) — front desk gets this.
  const canManageIntake = canViewMedical || can(PERM.OPD_PATIENT_INTAKE_MANAGE);
  // Permission-based (replaces role literals): recording vitals, prescribing, exporting, transferring.
  const canRecordReadings = isSuper || can(PERM.OPD_VITALS_RECORD);
  const canCreatePrescription = isSuper || can(PERM.OPD_PRESCRIPTIONS_CREATE);
  const canSeePrescriptions = canViewMedical;
  const canExportVitals = isSuper || can(PERM.REPORTS_EXPORT);
  const canUseAi = userData?.role === 'doctor';
  const [completingAppointment, setCompletingAppointment] = useState(false);

  const handleCompleteAppointment = async () => {
    if (!appointment?.id) return;
    setCompletingAppointment(true);
    try {
      await api.appointments.updateStatus(appointment.id, 'COMPLETED' as any);
      toast.success('Appointment marked as completed');
      // Refresh patient history so the status chip updates
      handleRefresh();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to complete appointment');
    } finally {
      setCompletingAppointment(false);
    }
  };

  const getAppointmentStatusColor = (status?: string) => {
    switch (status) {
      case 'WAITING':
        return { bg: '#f3f4f6', color: '#6b7280', label: 'Waiting' };
      case 'IN_PROGRESS':
        return { bg: '#fef3c7', color: '#92400e', label: 'In Progress' };
      case 'COMPLETED':
        return { bg: '#dcfce7', color: '#166534', label: 'Completed' };
      case 'SKIPPED':
        return { bg: '#fed7aa', color: '#9a3412', label: 'Skipped' };
      case 'NO_SHOW':
        return { bg: '#fecaca', color: '#991b1b', label: 'No Show' };
      case 'CANCELLED':
        return { bg: '#fee2e2', color: '#7f1d1d', label: 'Cancelled' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280', label: 'Unknown' };
    }
  };

  const generateAiSummary = async () => {
    if (!patient.id || loadingAiSummary) return;

    setLoadingAiSummary(true);
    try {
      safeConsole.log('🔄 Fetching case brief from server (backend handles caching)');
      const response = await api.ai.getPatientSummary(patient.id);
      if (response.data) {
        setAiSummary(response.data);
        toast.success('Case brief loaded');
      } else {
        throw new Error(response.error || 'Failed to generate summary');
      }
    } catch (error: any) {
      safeConsole.error('Failed to generate AI summary:', error);
      toast.error(error?.message || 'Failed to generate case brief');
    } finally {
      setLoadingAiSummary(false);
    }
  };

  const handlePrescriptionSuccess = () => {
    setPrescriptionWizardOpen(false);
    // Small delay to ensure database commit is complete
    setTimeout(() => {
      loadPrescriptions(); // Reload prescriptions list
      loadPatientHistory(); // Reload to show new prescription in history
    }, 300);
  };

  const handleViewDocument = async (filePath: string) => {
    try {
      const response = await api.labUpload.getPreviewUrl(filePath);
      if (response.data?.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      safeConsole.error('Failed to view document:', error);
    }
  };

  const handleAnalyzeLab = async (doc: any) => {
    setAnalyzingLabId(doc.id);
    try {
      const response = await api.ai.analyzeLabReport(doc.filePath, patient.id);
      if (response.data) {
        setLabAnalysis(response.data);
        setShowLabAnalysisDialog(true);
      } else {
        toast.error('AI was unable to extract data from this document');
      }
    } catch (error) {
      safeConsole.error('Failed to analyze lab report:', error);
      toast.error('Failed to analyze lab report. Please ensure it is a clear image of a report.');
    } finally {
      setAnalyzingLabId(null);
    }
  };

  const handleSaveVitals = async () => {
    setVitalsSaving(true);
    try {
      const response = await api.patients.update(patient.id, {
        bloodGroup: vitalFormData.bloodGroup || undefined,
        allergies: vitalFormData.allergies.filter((a: string) => a.trim()),
        chronicConditions: vitalFormData.chronicConditions.filter((c: string) => c.trim()),
      });
      
      if (response.data) {
        toast.success('Clinical information updated successfully');
        setEditingVitals(false);
        // Reload patient info from server to ensure data is up-to-date
        await loadPatientInfo();
      } else {
        toast.error(response.error || 'Failed to update patient information');
      }
    } catch (error) {
      safeConsole.error('Failed to save vitals:', error);
      toast.error('Failed to save clinical information');
    } finally {
      setVitalsSaving(false);
    }
  };

  const handleAddAllergy = () => {
    if (allergyInput.trim()) {
      setVitalFormData(prev => ({
        ...prev,
        allergies: [...prev.allergies, allergyInput.trim()]
      }));
      setAllergyInput('');
    }
  };

  const handleRemoveAllergy = (index: number) => {
    setVitalFormData(prev => ({
      ...prev,
      allergies: prev.allergies.filter((_: string, i: number) => i !== index)
    }));
  };

  const handleAddCondition = () => {
    if (conditionInput.trim()) {
      setVitalFormData(prev => ({
        ...prev,
        chronicConditions: [...prev.chronicConditions, conditionInput.trim()]
      }));
      setConditionInput('');
    }
  };

  const handleRemoveCondition = (index: number) => {
    setVitalFormData(prev => ({
      ...prev,
      chronicConditions: prev.chronicConditions.filter((_: string, i: number) => i !== index)
    }));
  };

  const canEditVitals = userData?.role && ['doctor', 'staff', 'clinic_admin'].includes(userData.role);

  const allLabDocuments = useMemo(() => {
    const attachedDocs = prescriptions
      .filter(p => p.labDocuments && p.labDocuments.length > 0)
      .flatMap(p => p.labDocuments!.map(doc => ({
        ...doc,
        doctorName: doctorMap.get(p.doctorId) || 'Consulting Doctor',
        prescriptionDate: p.createdAt,
        type: 'attached'
      })));

    // Standalone docs are now grouping sessions. We need to flatten them to show individual files.
    const standaloneDocs: any[] = [];
    standaloneDocuments.forEach(session => {
      if (session.documents && session.documents.length > 0) {
        session.documents.forEach((doc: any) => {
          standaloneDocs.push({
            id: doc.id,
            filePath: doc.filePath,
            fileName: doc.fileName || 'Uploaded Document',
            doctorName: doctorMap.get(session.doctorId) || 'Staff Upload',
            prescriptionDate: doc.createdAt || session.createdAt,
            type: 'standalone'
          });
        });
      } else if (session.filePath) {
        // Fallback for sessions with legacy single file logic
        standaloneDocs.push({
          id: session.id,
          filePath: session.filePath,
          fileName: 'Uploaded Document',
          doctorName: doctorMap.get(session.doctorId) || 'Staff Upload',
          prescriptionDate: session.createdAt,
          type: 'standalone'
        });
      }
    });

    // Merge and de-duplicate by filePath (preferring attached versions if they exist)
    const combined = [...attachedDocs];
    const existingPaths = new Set(attachedDocs.map(d => d.filePath));

    standaloneDocs.forEach(doc => {
      if (!existingPaths.has(doc.filePath)) {
        combined.push(doc);
      }
    });

    return combined.sort((a, b) => new Date(b.prescriptionDate).getTime() - new Date(a.prescriptionDate).getTime());
  }, [prescriptions, standaloneDocuments, doctorMap]);

  return (
    <>
      {/* ── Patient Details floating window ─────────────────────────────── */}
      {open && (
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            right: 20,
            top: 80,
            width: isMaximized ? 'calc(100vw - 40px)' : drawerWidth,
            height: isMaximized ? 'calc(100vh - 100px)' : drawerHeight,
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: isDark
              ? '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06), 0 0 60px rgba(25,118,210,0.18)'
              : '0 32px 80px rgba(0,0,0,0.22), 0 0 0 1.5px rgba(25,118,210,0.22), 0 0 40px rgba(25,118,210,0.1)',
            maxWidth: 'calc(100vw - 40px)',
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'hidden',
            transition: 'width 0.2s ease, height 0.2s ease',
          }}
        >
          {/* ── Gradient title bar ────────────────────────────────────────── */}
          <Box
            sx={{
              background: isDark
                ? 'linear-gradient(135deg, #0d2851 0%, #0a3d7a 50%, #0b4d9e 100%)'
                : 'linear-gradient(135deg, #1149a0 0%, #1565c0 40%, #1976d2 100%)',
              flexShrink: 0,
              userSelect: 'none',
            }}
          >
            {/* Window chrome row: traffic lights + label + refresh */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5, pb: 0.75 }}>
              {/* macOS-style traffic lights */}
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                <Tooltip title={t('close')} placement="bottom">
                  <Box
                    onClick={onClose}
                    sx={{
                      width: 13, height: 13, borderRadius: '50%',
                      bgcolor: '#ff5f57',
                      cursor: 'pointer',
                      border: '1px solid rgba(0,0,0,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      '&:hover': { filter: 'brightness(0.85)' },
                    }}
                  />
                </Tooltip>
                <Tooltip title={t('minimize')} placement="bottom">
                  <Box
                    onClick={() => minimizePatient(patient.id)}
                    sx={{
                      width: 13, height: 13, borderRadius: '50%',
                      bgcolor: '#ffbd2e',
                      cursor: 'pointer',
                      border: '1px solid rgba(0,0,0,0.12)',
                      '&:hover': { filter: 'brightness(0.85)' },
                    }}
                  />
                </Tooltip>
                <Tooltip title={isMaximized ? 'Restore' : 'Maximize'} placement="bottom">
                  <Box
                    onClick={() => setIsMaximized(!isMaximized)}
                    sx={{
                      width: 13, height: 13, borderRadius: '50%',
                      bgcolor: '#28c840',
                      cursor: 'pointer',
                      border: '1px solid rgba(0,0,0,0.12)',
                      '&:hover': { filter: 'brightness(0.85)' },
                    }}
                  />
                </Tooltip>
              </Box>

              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500, letterSpacing: 0.6, fontSize: '0.68rem' }}
              >
                {t('patientRecord')}
              </Typography>

              <Tooltip title={t('refresh')} placement="bottom">
                <IconButton
                  size="small"
                  onClick={handleRefresh}
                  disabled={loading || loadingPrescriptions}
                  sx={{ color: 'rgba(255,255,255,0.7)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                >
                  <RefreshCw size={14} className={loading || loadingPrescriptions ? 'animate-spin' : ''} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Patient avatar + name + basic info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, pb: 2, pt: 0.25 }}>
              {/* Initials avatar */}
              <Box
                sx={{
                  width: 52, height: 52, borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.18)',
                  border: '2px solid rgba(255,255,255,0.35)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                }}
              >
                <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.25rem', lineHeight: 1 }}>
                  {patient.name?.charAt(0)?.toUpperCase() || '?'}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.3 }}>
                    {patient.name}
                  </Typography>
                  {appointment && (
                    <Chip
                      label={getAppointmentStatusColor(appointment.status).label}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.18)',
                        color: 'rgba(255,255,255,0.95)',
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        height: 20,
                        border: '1px solid rgba(255,255,255,0.28)',
                        '& .MuiChip-label': { px: '6px' },
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)', display: 'block', mt: 0.3, fontSize: '0.78rem' }}>
                  {[
                    patient.age ? `${patient.age} yrs` : null,
                    patient.gender,
                    patient.mobile,
                  ].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* ── Action bar ───────────────────────────────────────────────── */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
              px: 2,
              py: 1,
              bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(25,118,210,0.03)',
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            {appointment && (appointment.status === 'WAITING' || appointment.status === 'IN_PROGRESS') && (
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={completingAppointment ? <CircularProgress size={13} color="inherit" /> : <CheckCircle size={13} />}
                onClick={handleCompleteAppointment}
                disabled={completingAppointment}
                sx={{ borderRadius: 5, fontSize: '0.75rem', py: 0.5 }}
              >
                {completingAppointment ? 'Completing…' : 'Complete'}
              </Button>
            )}
            {canUseAi && (
              <Button
                variant="contained"
                size="small"
                startIcon={loadingAiSummary ? <CircularProgress size={13} color="inherit" /> : <Sparkles size={13} />}
                onClick={generateAiSummary}
                disabled={loadingAiSummary}
                sx={{
                  borderRadius: 5,
                  fontSize: '0.75rem',
                  py: 0.5,
                  background: 'linear-gradient(45deg, #7b1fa2 30%, #4a148c 90%)',
                  color: '#fff',
                  '&:hover': { background: 'linear-gradient(45deg, #6a1b9a 30%, #311b92 90%)', color: '#fff' },
                }}
              >
                {t('aiCaseBrief')}
              </Button>
            )}
            {canCreatePrescription && appointment && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Pill size={13} />}
                onClick={() => {
                  setSelectedVisitId(appointment.id);
                  setPrescriptionWizardOpen(true);
                }}
                sx={{ borderRadius: 5, fontSize: '0.75rem', py: 0.5 }}
              >
                {t('addRx')}
              </Button>
            )}
            {canExportVitals && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download size={13} />}
              onClick={() => navigate(`/reports/vitals-export?patientId=${patient.id}`)}
              sx={{ borderRadius: 5, fontSize: '0.75rem', py: 0.5 }}
            >
              {t('exportVitals')}
            </Button>
            )}
            {!isOwnPatient && patient.visibilityMode === 'CONSENT_REQUIRED'
              && (userData?.role === 'clinic_admin' || userData?.role === 'doctor' || userData?.role === 'staff') && (
              <Button
                variant="outlined"
                size="small"
                color="info"
                startIcon={<ArrowRightLeft size={13} />}
                onClick={() => setShowConsentRequestDialog(true)}
                sx={{ borderRadius: 5, fontSize: '0.75rem', py: 0.5 }}
              >
                {t('requestAccess')}
              </Button>
            )}
            {(userData?.role === 'clinic_admin' || userData?.role === 'staff' || userData?.role === 'super_admin') && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<ShieldOff size={13} />}
                onClick={() => setShowErasureDialog(true)}
                sx={{ borderRadius: 5, fontSize: '0.75rem', py: 0.5 }}
              >
                {t('requestDataErasure')}
              </Button>
            )}
          </Box>

          {/* ── Scrollable content ───────────────────────────────────────── */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
            {aiSummary && <PatientAISummaryPanel aiSummary={aiSummary} isDark={isDark} />}

            <Stack spacing={1} sx={{ mt: aiSummary ? 2 : 0 }}>
              {canManageIntake && (
                <PatientMedicalInfoSection
                  expanded={expanded}
                  onExpandChange={(panel) => setExpanded(expanded === panel ? false : panel)}
                  displayPatient={displayPatient}
                  patient={patient}
                  editingVitals={editingVitals}
                  setEditingVitals={setEditingVitals}
                  vitalsSaving={vitalsSaving}
                  vitalFormData={vitalFormData}
                  setVitalFormData={setVitalFormData}
                  allergyInput={allergyInput}
                  setAllergyInput={setAllergyInput}
                  conditionInput={conditionInput}
                  setConditionInput={setConditionInput}
                  handleSaveVitals={handleSaveVitals}
                  handleAddAllergy={handleAddAllergy}
                  handleRemoveAllergy={handleRemoveAllergy}
                  handleAddCondition={handleAddCondition}
                  handleRemoveCondition={handleRemoveCondition}
                  canEditVitals={canEditVitals}
                  isDark={isDark}
                />
              )}

              <PatientVisitHistorySection
                expanded={expanded}
                onExpandChange={(panel) => setExpanded(expanded === panel ? false : panel)}
                visits={visits}
                loading={loading}
                appointment={appointment}
                showVitalsForm={showVitalsForm}
                setShowVitalsForm={setShowVitalsForm}
                currentVisitHasVitals={currentVisitHasVitals}
                onLatestVitalsStatus={setCurrentVisitHasVitals}
                canRecordReadings={canRecordReadings}
                loadPatientHistory={loadPatientHistory}
                patientId={patient.id}
              />

              <PatientPrescriptionsSection
                expanded={expanded}
                onExpandChange={(panel) => setExpanded(expanded === panel ? false : panel)}
                canSeePrescriptions={canSeePrescriptions}
                prescriptions={prescriptions}
                loadingPrescriptions={loadingPrescriptions}
                setSelectedPrescription={setSelectedPrescription}
                setShowPrescriptionView={setShowPrescriptionView}
                processingPdfId={processingPdfId}
                setProcessingPdfId={setProcessingPdfId}
              />

              {canViewLabs && (
                <PatientDiagnosticsSection
                  expanded={expanded}
                  onExpandChange={(panel) => setExpanded(expanded === panel ? false : panel)}
                  canRecordReadings={canRecordReadings}
                  onUploadClick={() => setShowLabUploadModal(true)}
                  sortedVisits={sortedVisits}
                  selectedVisitId={selectedVisitId}
                  setSelectedVisitId={setSelectedVisitId}
                  allLabDocuments={allLabDocuments}
                  analyzingLabId={analyzingLabId}
                  handleAnalyzeLab={handleAnalyzeLab}
                  handleViewDocument={handleViewDocument}
                  isDark={isDark}
                  patientId={patient.id}
                />
              )}

              {canViewMedical && (
                <AccordionSection
                  panelId="trends"
                  expanded={expanded}
                  onExpandChange={(panel) => setExpanded(expanded === panel ? false : panel)}
                  icon={<TrendingUp size={20} color={theme.palette.secondary.main} />}
                  title={t('clinicalParameterTrends')}
                >
                  <LabTrends patientId={patient.id} visits={sortedVisits} />
                </AccordionSection>
              )}

              {canViewMedical && (specialtyTemplateKey === 'GYNECOLOGY_OB' || specialtyTemplateKey === 'PEDIATRICS') && (
                <AccordionSection
                  panelId="specialty-care"
                  expanded={expanded}
                  onExpandChange={(panel) => setExpanded(expanded === panel ? false : panel)}
                  icon={<Stethoscope size={20} color={theme.palette.secondary.main} />}
                  title={t('specialtyCare')}
                >
                  <SpecialtyCarePanel patientId={patient.id} templateKey={specialtyTemplateKey} />
                </AccordionSection>
              )}
            </Stack>
          </Box>
        </Paper>
      )}

      {/* ── Supporting dialogs ─────────────────────────────────────────── */}
      <FollowUpModal
        open={showFollowUpModal}
        onClose={() => setShowFollowUpModal(false)}
        patient={patient}
        onFollowUpScheduled={() => setShowFollowUpModal(false)}
      />

      <LabUploadModal
        open={showLabUploadModal}
        onClose={() => setShowLabUploadModal(false)}
        patientId={patient.id}
        onUploadComplete={() => {
          loadStandaloneDocuments();
          setShowLabUploadModal(false);
        }}
      />

      {canCreatePrescription && (
        <PrescriptionWizard
          open={prescriptionWizardOpen}
          onClose={() => setPrescriptionWizardOpen(false)}
          patientId={patient.id}
          patientName={patient.name}
          visitId={selectedVisitId || undefined}
          onSuccess={handlePrescriptionSuccess}
        />
      )}

      {selectedPrescription && (
        <PrescriptionViewDialog
          open={showPrescriptionView}
          onClose={() => {
            setShowPrescriptionView(false);
            setSelectedPrescription(null);
          }}
          prescription={selectedPrescription}
          onUpdate={loadPrescriptions}
          onEdit={() => {
            setShowPrescriptionView(false);
            if (selectedPrescription) setSelectedVisitId(selectedPrescription.visitId);
            setPrescriptionWizardOpen(true);
          }}
        />
      )}

      <PatientLabAnalysisDialog
        open={showLabAnalysisDialog}
        onClose={() => setShowLabAnalysisDialog(false)}
        labAnalysis={labAnalysis}
      />

      <Dialog open={showErasureDialog} onClose={() => setShowErasureDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Data Erasure — {patient.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            {t('thisSubmitsARequestForA')}
          </Alert>
          <TextField
            label={t('reasonHowThisRequestWasReceived')} fullWidth multiline minRows={2}
            value={erasureReason} onChange={(e) => setErasureReason(e.target.value)}
            placeholder={t('eGPatientCalledRequestingTheir')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowErasureDialog(false)}>{t('cancel')}</Button>
          <Button
            variant="contained" color="error" disabled={submittingErasure}
            startIcon={submittingErasure ? <CircularProgress size={14} /> : undefined}
            onClick={async () => {
              setSubmittingErasure(true);
              try {
                await api.erasure.submitByStaff(patient.id, erasureReason.trim());
                toast.success('Erasure request submitted for review.');
                setShowErasureDialog(false);
                setErasureReason('');
              } catch (err: any) {
                toast.error(err?.message ?? 'Failed to submit request');
              } finally {
                setSubmittingErasure(false);
              }
            }}
          >
            {t('submitRequest')}
          </Button>
        </DialogActions>
      </Dialog>

      {!isOwnPatient && patient.globalPatientId && (
        <ConsentRequestDialog
          open={showConsentRequestDialog}
          onClose={() => setShowConsentRequestDialog(false)}
          sourceClinicId={patient.clinicId ?? ''}
          patient={{
            found: true,
            globalPatientId: patient.globalPatientId,
            maskedName: patient.name,
            maskedMobile: patient.mobile ?? '',
          }}
          onConsentCreated={() => {
            setShowConsentRequestDialog(false);
            toast.success('Access request sent — patient will receive an approval request');
          }}
        />
      )}
    </>
  );
}